import { useI18n } from 'next-localization';

import { ProviderLocation } from '@/features/profile/profile.types';
import { useDistanceContext } from '@/hooks/use-distance-context';
import { useProviderLocation } from '@/hooks/use-provider-location';
import { cn } from '@/lib/utils';
import { NeuModal } from '@hcahealthcaredpx/neutron-react';
import { AzureMap } from '@xsf/shared-lib/components/azure-map';
import { HCALocation } from '@xsf/shared-lib/types/search';

import { useModalContext } from '../modals/modal-context';
import { LocationsList } from './locations-list';
import styles from './modal.module.css';

export const ChangeLocationListModal = (): JSX.Element | null => {
  const {
    providerLocations,
    activeProviderId,
    setActiveProviderLocationByDoctorId,
    setActiveProviderId,
  } = useDistanceContext();
  const { isOpen, closeModal } = useModalContext();
  const { t } = useI18n();
  const getProviderActiveLocation = useProviderLocation();

  const close = () => {
    closeModal('change-location-list-modal');
    setActiveProviderId('');
  };

  const chooseLocationHandler = (location: ProviderLocation) => {
    setActiveProviderLocationByDoctorId((prev) => {
      const updatedLocations = {
        ...prev,
        [activeProviderId]: location.id,
      };

      sessionStorage.setItem('activeProviderLocations', JSON.stringify(updatedLocations));

      return updatedLocations;
    });
  };

  if (!providerLocations || providerLocations.length === 0) {
    return null;
  }

  const providerLocation = getProviderActiveLocation(
    providerLocations as ProviderLocation[],
    activeProviderId
  );

  const mappedLocations: HCALocation[] = providerLocations.map((location) => ({
    facilityId: location.id,
    locationName: location.name,
    longitude: location.longitude,
    latitude: location.latitude,
    marketedFacilityName: location.name,
    distance: Number(location.distance),
    coid: location.hospitalAffiliationCOID,
    //  Needed to show the pin
    marketedSecondaryTypeNames: [''],
    marketedPrimaryPhoneNumber: location.phone,
    city: location.city,
    postalCode: location.zip,
    state: location.state,
    street1: location.street,
  }));

  const isSingleLocation = providerLocations.length === 1;

  const selectedLocationId = providerLocation?.id ?? '';

  return (
    <div className={styles.modalWrapper}>
      <NeuModal
        active={isOpen('change-location-list-modal')}
        size="large"
        onNeuDismiss={close}
        label={isSingleLocation ? t('OAS.Listing.LocationDetails') : t('Header.ChangeLocation')}
        backdropDismiss
      >
        <NeuModal.Content>
          <div className="row pb-2">
            <div className="col-12 col-sm-6 pe-0">
              <div className={cn(styles.azureMap, 'w-100 overflow-hidden d-none d-sm-block')}>
                <AzureMap locations={mappedLocations} />
              </div>
            </div>
            <div className="col-12 col-sm-6 ps-0">
              <LocationsList
                locations={providerLocations}
                chooseLocationHandler={chooseLocationHandler}
                closeModal={() => close()}
                selectedId={selectedLocationId}
              />
            </div>
          </div>
        </NeuModal.Content>
      </NeuModal>
    </div>
  );
};
